import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAnalyticsOverview,
  getAnalyticsRevenue,
  getAnalyticsProducts,
  getAnalyticsCustomers,
  getAnalyticsFeatures,
  getOrdersByStatus,
  getPaymentMethods,
  getRevenueReport,
  getOrdersReport,
  getAttributionReport,
  getOrderIntelligenceReport,
  getCustomerIntelligenceReport,
  getProductIntelligenceReport,
  getSalesBreakdown,
  getAbandonedCarts,
  getAlerts,
  getProductsReport,
  getCategoriesReport,
  getCouponsReport,
  getTaxesReport,
  getStockReport,
  getCustomersReport,
  getAnalyticsSettings,
  updateAnalyticsSettings,
  syncAnalyticsSettings,
} from '@/services/analytics.api';
import type { SiteAnalyticsSettings } from '@wooconnect/shared';

export function useAnalyticsOverview(siteId: string | null) {
  return useQuery({
    queryKey: ['analyticsOverview', siteId],
    queryFn: () => getAnalyticsOverview(siteId!),
    enabled: !!siteId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAnalyticsRevenue(siteId: string | null, days: number) {
  return useQuery({
    queryKey: ['analyticsRevenue', siteId, days],
    queryFn: () => getAnalyticsRevenue(siteId!, days),
    enabled: !!siteId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAnalyticsProducts(siteId: string | null) {
  return useQuery({
    queryKey: ['analyticsProducts', siteId],
    queryFn: () => getAnalyticsProducts(siteId!),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnalyticsCustomers(siteId: string | null) {
  return useQuery({
    queryKey: ['analyticsCustomers', siteId],
    queryFn: () => getAnalyticsCustomers(siteId!),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnalyticsFeatures(siteId: string | null) {
  return useQuery({
    queryKey: ['analyticsFeatures', siteId],
    queryFn: () => getAnalyticsFeatures(siteId!),
    enabled: !!siteId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useOrdersByStatus(siteId: string | null) {
  return useQuery({
    queryKey: ['ordersByStatus', siteId],
    queryFn: () => getOrdersByStatus(siteId!),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePaymentMethods(siteId: string | null) {
  return useQuery({
    queryKey: ['paymentMethods', siteId],
    queryFn: () => getPaymentMethods(siteId!),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

interface DateRangeParams {
  after: string;
  before: string;
  interval?: string;
  compare?: string;
  status_is?: string;
}

export function useRevenueReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['revenueReport', siteId, params?.after, params?.before, params?.interval, params?.compare, params?.status_is],
    queryFn: () => getRevenueReport(siteId!, {
      after: params!.after,
      before: params!.before,
      interval: params!.interval || 'day',
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useOrdersReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['ordersReport', siteId, params?.after, params?.before, params?.interval, params?.compare, params?.status_is],
    queryFn: () => getOrdersReport(siteId!, {
      after: params!.after,
      before: params!.before,
      interval: params!.interval || 'day',
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useAttributionReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['attributionReport', siteId, params?.after, params?.before, params?.compare, params?.status_is],
    queryFn: () => getAttributionReport(siteId!, {
      after: params!.after,
      before: params!.before,
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useOrderIntelligenceReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['orderIntelligenceReport', siteId, params?.after, params?.before, params?.compare, params?.status_is],
    queryFn: () => getOrderIntelligenceReport(siteId!, {
      after: params!.after,
      before: params!.before,
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useCustomerIntelligenceReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['customerIntelligenceReport', siteId, params?.after, params?.before, params?.compare, params?.status_is],
    queryFn: () => getCustomerIntelligenceReport(siteId!, {
      after: params!.after,
      before: params!.before,
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useProductIntelligenceReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['productIntelligenceReport', siteId, params?.after, params?.before, params?.status_is, params?.compare],
    queryFn: () => getProductIntelligenceReport(siteId!, {
      after: params!.after,
      before: params!.before,
      status_is: params!.status_is,
      compare: params!.compare || 'previousPeriod',
    }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useSalesBreakdown(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['salesBreakdown', siteId, params?.after, params?.before, params?.status_is, params?.compare],
    queryFn: () => getSalesBreakdown(siteId!, {
      after: params!.after,
      before: params!.before,
      status_is: params!.status_is,
      compare: params!.compare || 'previousPeriod',
    }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useAbandonedCarts(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['abandonedCarts', siteId, params?.after, params?.before],
    queryFn: () => getAbandonedCarts(siteId!, { after: params!.after, before: params!.before }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useAlerts(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['alerts', siteId, params?.after, params?.before, params?.compare, params?.status_is],
    queryFn: () => getAlerts(siteId!, { after: params!.after, before: params!.before, compare: params!.compare || 'previousPeriod', status_is: params!.status_is }),
    enabled: !!siteId && !!params,
    staleTime: 3 * 60 * 1000,
  });
}

export function useProductsReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['productsReport', siteId, params?.after, params?.before, params?.compare, params?.status_is],
    queryFn: () => getProductsReport(siteId!, {
      after: params!.after,
      before: params!.before,
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCategoriesReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['categoriesReport', siteId, params?.after, params?.before, params?.compare],
    queryFn: () => getCategoriesReport(siteId!, {
      after: params!.after,
      before: params!.before,
      compare: params!.compare || 'previousPeriod',
    }),
    enabled: !!siteId && !!params,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCouponsReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['couponsReport', siteId, params?.after, params?.before, params?.compare, params?.status_is],
    queryFn: () => getCouponsReport(siteId!, {
      after: params!.after,
      before: params!.before,
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTaxesReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['taxesReport', siteId, params?.after, params?.before, params?.compare, params?.status_is],
    queryFn: () => getTaxesReport(siteId!, {
      after: params!.after,
      before: params!.before,
      compare: params!.compare || 'previousPeriod',
      status_is: params!.status_is,
    }),
    enabled: !!siteId && !!params,
    staleTime: 5 * 60 * 1000,
  });
}

export function useStockReport(siteId: string | null, type?: string) {
  return useQuery({
    queryKey: ['stockReport', siteId, type],
    queryFn: () => getStockReport(siteId!, type ? { type } : undefined),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomersReport(siteId: string | null, params: DateRangeParams | null) {
  return useQuery({
    queryKey: ['customersReport', siteId, params?.after, params?.before],
    queryFn: () => getCustomersReport(siteId!, {
      after: params!.after,
      before: params!.before,
    }),
    enabled: !!siteId && !!params,
    staleTime: 5 * 60 * 1000,
  });
}

// Analytics Settings hooks
export function useAnalyticsSettings(siteId: string | null) {
  return useQuery({
    queryKey: ['analyticsSettings', siteId],
    queryFn: () => getAnalyticsSettings(siteId!),
    enabled: !!siteId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useUpdateAnalyticsSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, settings }: { siteId: string; settings: Partial<SiteAnalyticsSettings> }) =>
      updateAnalyticsSettings(siteId, settings),
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries({ queryKey: ['analyticsSettings', siteId] });
    },
  });
}

export function useSyncAnalyticsSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => syncAnalyticsSettings(siteId),
    onSuccess: (_, siteId) => {
      queryClient.invalidateQueries({ queryKey: ['analyticsSettings', siteId] });
    },
  });
}
